import React from 'react';

import ContentCard, { TrackingSource } from '../content-card';
import CardImage from '../card-image';
import ProductImages from '../../toplists/components/product-images';

import { compareByDate } from '../../utils';

import { ExpertItems, Guide, Test, Toplist } from './types';
import { ContentTypes } from '../../types';
import { AdvizedCategory, ToplistHeadline } from 'src/types';

type Props = {
  className?: string;
  onClick?: (onClickData: {
    id: string;
    title: string;
    type: string;
    url: string;
    index: number;
  }) => void;
  items: ExpertItems;
  imageSize?: number;
  limit?: number;
  offset?: number;
  hideExperts?: boolean;
  category?: AdvizedCategory;
  trackingSource?: TrackingSource;
};

export default function ContentCards({
  category,
  className,
  onClick,
  items,
  imageSize = 130,
  limit = 0,
  offset = 0,
  hideExperts,
  trackingSource,
}: Props) {
  const numberOfCards = limit ? limit + offset : items.length;

  const getContentCard = (
    item: Toplist | ToplistHeadline | Test | Guide,
    index: number
  ) => {
    switch (item.__typename) {
      case ContentTypes.TOPLIST_HEADLINE: {
        if (!item.toplists || !item.toplists[0]) {
          return null;
        }
        return (
          <ContentCard
            category={category}
            className={className}
            date={item.toplists[0].date}
            experts={
              !hideExperts && item.toplists[0].expert.url
                ? [
                    item.toplists[0].expert,
                    ...(item.toplists[0]?.headlineInfo?.otherExperts || []),
                  ]
                : undefined
            }
            onClick={() =>
              onClick?.({
                id: item.id,
                title: item.name,
                type: ContentTypes.TOPLIST_HEADLINE,
                url: item.url,
                index,
              })
            }
            key={item.id}
            likes={item.toplists[0].headlineInfo?.likes}
            media={
              <ProductImages
                height={`${imageSize}px`}
                background={false}
                selectedPosition={2}
                products={item.toplists[0]?.products?.map(p => ({
                  ...p,
                }))}
              />
            }
            title={item.name}
            trackingSource={trackingSource}
            type={ContentTypes.TOPLIST_HEADLINE}
            url={item.url}
          />
        );
      }

      case ContentTypes.TOPLIST: {
        return (
          <ContentCard
            category={category}
            className={className}
            date={item.date}
            experts={
              !hideExperts && item.expert?.url
                ? [item.expert, ...(item.headlineInfo?.otherExperts || [])]
                : undefined
            }
            onClick={() =>
              onClick?.({
                id: item.id,
                title: item.title,
                type: ContentTypes.TOPLIST,
                url: item.headlineInfo.url,
                index,
              })
            }
            key={item.id}
            likes={item.headlineInfo?.likes}
            media={
              <ProductImages
                height={`${imageSize}px`}
                products={item.products.map(p => ({ ...p }))}
                background={false}
              />
            }
            title={item.title}
            trackingSource={trackingSource}
            type={ContentTypes.TOPLIST}
            url={item.headlineInfo.url}
          />
        );
      }

      case ContentTypes.TEST: {
        return (
          <ContentCard
            category={category}
            className={className}
            date={item.date}
            experts={!hideExperts ? [item.expert] : undefined}
            grade={item.grade}
            onClick={() =>
              onClick?.({
                id: item.id,
                title: item.title,
                type: ContentTypes.TEST,
                url: item.url,
                index,
              })
            }
            key={item.id}
            likes={item.likes}
            media={
              item.imageWithSize && (
                <CardImage
                  src={item.imageWithSize.src}
                  alt={item.imageWithSize.alt}
                  height={imageSize}
                />
              )
            }
            title={item.title}
            trackingSource={trackingSource}
            type={ContentTypes.TEST}
            url={item.url}
          />
        );
      }

      case ContentTypes.GUIDE: {
        return (
          <ContentCard
            category={category}
            className={className}
            date={item.date}
            experts={!hideExperts ? [item.expert] : undefined}
            onClick={() =>
              onClick?.({
                id: item.id,
                title: item.title,
                type: ContentTypes.GUIDE,
                url: item.url,
                index,
              })
            }
            key={item.id}
            likes={item.likes}
            media={
              item.image && (
                <CardImage
                  src={item.image.src}
                  alt={item.image.alt}
                  height={imageSize}
                />
              )
            }
            title={item.title}
            trackingSource={trackingSource}
            type={ContentTypes.GUIDE}
            url={item.url}
          />
        );
      }

      default:
        return null;
    }
  };

  return (
    <>
      {items.sort(compareByDate).map((item, index) => {
        if (!item || !item.id || !item.__typename) {
          return null;
        }

        if (index < offset || index >= numberOfCards) {
          return null;
        }

        return getContentCard(item, index);
      })}
    </>
  );
}
